import { Dialog } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import intl from "react-intl-universal";
import { TbCirclePlus } from "react-icons/tb";
import { CurrencyIcon } from "../../../../../Components/Atoms/CurrencyIcon";
import { ModalCloseButton } from "../../../../../Components/Molecules/ValutDialog";
import { useBaasInstance } from "../../../../../Contexts/BaasInstanceContext";
import { useToast } from "../../../../../Hooks/toast";
import { collectPinnedShellAccountIds } from "../../../../../Services/baasAccountsApi";
import { fetchBaasAccounts } from "../../../../../Services/baasAccountsListApi";
import { fetchInternalTransferOptions } from "../../../../../Services/baasInternalTransfersApi";
import {
  BAAS_STABLES_CREATE_ASSETS,
  buildBaasStableCdnIconUrl,
  createBaasStablesWallet,
  fetchBaasStablesAssetNetworks,
  formatBaasStablesNetworkLabel,
  getBaasMissingCreateAssetsForAccount,
  getBaasStablesCreateNetworksForAsset,
  pickDefaultBaasCreateNetwork,
  resolveBaasPublicBaseUrl,
  resolveBaasStableIconSymbol,
} from "../../../../../Services/baasStablesWalletApi";
import { isVirtualInstanceShellAccount } from "../../../../../Services/baasVirtualShells";

const MODAL_CLASS =
  "!rounded-2xl !shadow-card app-card !m-4 !flex !flex-col !w-[calc(100%-2rem)] !max-w-lg";

const inputClass =
  "h-10 w-full rounded-lg border border-gray-200 px-3 text-sm text-brand-dark outline-none transition focus:border-[#e7a73f] dark:border-brand-dark-border dark:bg-brand-dark-elevated dark:text-gray-100";

const chipClass = (selected) =>
  [
    "inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-xs font-semibold transition",
    selected
      ? "border-[#e7a73f] bg-[#e7a73f]/15 text-[#b87a1f] dark:text-[#e7a73f]"
      : "border-gray-200 text-gray-600 hover:border-gray-300 dark:border-brand-dark-border dark:text-gray-300",
  ].join(" ");

const DEFAULT_ASSET = "USDC";
const DEFAULT_NETWORKS = getBaasStablesCreateNetworksForAsset(DEFAULT_ASSET);

export const BaasStablesWalletCreateModal = ({
  open,
  onClose,
  onCreated,
  wallets = [],
  initialAsset,
}) => {
  const { addToast } = useToast();
  const { selectedInstance } = useBaasInstance();

  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState("");
  const [asset, setAsset] = useState(DEFAULT_ASSET);
  const [network, setNetwork] = useState(
    () => pickDefaultBaasCreateNetwork(DEFAULT_NETWORKS, "Polygon")
  );
  const [networks, setNetworks] = useState(DEFAULT_NETWORKS);
  const [loading, setLoading] = useState(false);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [networksLoading, setNetworksLoading] = useState(false);
  const [baasBaseUrl, setBaasBaseUrl] = useState("");
  const [formError, setFormError] = useState(null);

  const availableAssets = useMemo(
    () => getBaasMissingCreateAssetsForAccount(wallets, accountId),
    [wallets, accountId],
  );
  const isAddMore =
    Boolean(accountId) && availableAssets.length < BAAS_STABLES_CREATE_ASSETS.length;

  useEffect(() => {
    let cancelled = false;

    if (!open || !selectedInstance) {
      setAccounts([]);
      setBaasBaseUrl("");
      return undefined;
    }

    setAccountsLoading(true);
    (async () => {
      try {
        const [next, publicBase, transferOptions] = await Promise.all([
          fetchBaasAccounts(selectedInstance),
          resolveBaasPublicBaseUrl(selectedInstance),
          fetchInternalTransferOptions(selectedInstance).catch(() => null),
        ]);
        const pinnedShellIds = collectPinnedShellAccountIds(transferOptions);
        const eligible = next.filter(
          (account) =>
            !pinnedShellIds.has(String(account?.id || "").trim()) &&
            !isVirtualInstanceShellAccount(account),
        );
        if (!cancelled) {
          setAccounts(eligible);
          setBaasBaseUrl(publicBase || "");
        }
      } catch {
        if (!cancelled) {
          setAccounts([]);
          setBaasBaseUrl("");
        }
      } finally {
        if (!cancelled) setAccountsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, selectedInstance]);

  useEffect(() => {
    if (!open) {
      setAccountId("");
      setAsset(DEFAULT_ASSET);
      setNetworks(DEFAULT_NETWORKS);
      setNetwork(pickDefaultBaasCreateNetwork(DEFAULT_NETWORKS, "Polygon"));
      setLoading(false);
      setNetworksLoading(false);
      setFormError(null);
      return;
    }

    setAccountId(accounts[0]?.id || "");
    setLoading(false);
    setFormError(null);
  }, [open, accounts]);

  useEffect(() => {
    if (!open) return;
    const preferred = String(initialAsset || "").trim().toUpperCase();
    setAsset((current) => {
      if (preferred && availableAssets.includes(preferred)) return preferred;
      if (availableAssets.includes(current)) return current;
      return availableAssets[0] || "";
    });
  }, [open, availableAssets, initialAsset]);

  useEffect(() => {
    if (!open || !asset) {
      if (open && !asset) {
        setNetworks([]);
        setNetwork("");
        setNetworksLoading(false);
      }
      return undefined;
    }

    let cancelled = false;
    const fallback = getBaasStablesCreateNetworksForAsset(asset);

    setNetworksLoading(true);
    setNetworks(fallback);
    setNetwork((current) => pickDefaultBaasCreateNetwork(fallback, current));

    (async () => {
      try {
        const next = await fetchBaasStablesAssetNetworks(selectedInstance, asset);
        if (cancelled) return;
        setNetworks(next);
        setNetwork((current) => pickDefaultBaasCreateNetwork(next, current));
      } catch {
        if (cancelled) return;
        setNetworks(fallback);
        setNetwork((current) => pickDefaultBaasCreateNetwork(fallback, current));
      } finally {
        if (!cancelled) setNetworksLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, asset, selectedInstance]);

  const canSubmit =
    Boolean(accountId) &&
    Boolean(asset) &&
    Boolean(network) &&
    availableAssets.length > 0 &&
    networks.length > 0 &&
    !loading &&
    !networksLoading;

  const handleClose = () => {
    if (loading) return;
    onClose?.();
  };

  const handleSelectAsset = (code) => {
    if (loading || code === asset) return;
    setAsset(code);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canSubmit || !selectedInstance) return;

    setLoading(true);
    setFormError(null);
    try {
      const wallet = await createBaasStablesWallet(selectedInstance, {
        accountId,
        asset,
        network,
      });

      addToast({
        type: "success",
        title: intl.get("baas.stablesWallet.create.success"),
      });
      onCreated?.(wallet);
      onClose?.();
    } catch (error) {
      const code = error?.code || error?.message;
      setFormError(
        code === "account_not_found"
          ? intl.get("baas.stablesWallet.create.accountNotFound")
          : intl.get("baas.stablesWallet.create.error"),
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ className: MODAL_CLASS }}
      disableEscapeKeyDown={loading}
    >
      <form onSubmit={handleSubmit} className="flex min-h-0 flex-col">
        <div className="relative border-b border-gray-100 px-5 py-4 dark:border-brand-dark-border sm:px-6">
          <ModalCloseButton
            onClick={handleClose}
            className="absolute right-3 top-3"
            disabled={loading}
          />
          <div className="flex items-center gap-2 pr-8">
            <TbCirclePlus className="h-5 w-5 shrink-0 text-[#e7a73f]" aria-hidden />
            <h2 className="text-lg font-bold text-brand-dark dark:text-gray-100">
              {intl.get("baas.stablesWallet.create.title")}
            </h2>
          </div>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {intl.get(
              isAddMore
                ? "baas.stablesWallet.create.subtitleAdd"
                : "baas.stablesWallet.create.subtitle",
            )}
          </p>
        </div>

        <div className="space-y-4 px-5 py-5 sm:px-6">
          {formError ? (
            <p
              role="alert"
              className="rounded-xl border border-red-200 bg-red-50/80 px-4 py-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300"
            >
              {formError}
            </p>
          ) : null}
          <div>
            <label
              htmlFor="baas-stables-create-account"
              className="mb-1.5 block text-xs font-medium text-gray-500 dark:text-gray-400"
            >
              {intl.get("baas.stablesWallet.create.accountLabel")}
            </label>
            <select
              id="baas-stables-create-account"
              className={inputClass}
              value={accountId}
              onChange={(event) => setAccountId(event.target.value)}
              required
              disabled={accountsLoading || loading}
            >
              <option value="">
                {accountsLoading
                  ? intl.get("baas.stablesWallet.create.accountsLoading")
                  : intl.get("baas.stablesWallet.create.accountPlaceholder")}
              </option>
              {accounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.entityName
                    ? `${account.entityName} — ${account.alias || account.accountNumber}`
                    : account.alias || account.accountNumber || account.id}
                </option>
              ))}
            </select>
            {!accountsLoading && accounts.length === 0 ? (
              <p className="mt-1.5 text-[11px] text-amber-700 dark:text-amber-400">
                {intl.get("baas.stablesWallet.create.noAccounts")}
              </p>
            ) : null}
          </div>

          <div>
            <p className="mb-1.5 text-xs font-medium text-gray-500 dark:text-gray-400">
              {intl.get("baas.stablesWallet.create.assetLabel")}
            </p>
            {availableAssets.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {intl.get("baas.stablesWallet.create.noRemainingAssets")}
              </p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {availableAssets.map((code) => {
                  const selected = asset === code;
                  const iconSymbol = resolveBaasStableIconSymbol(code);
                  return (
                    <button
                      key={code}
                      type="button"
                      className={chipClass(selected)}
                      aria-pressed={selected}
                      onClick={() => handleSelectAsset(code)}
                      disabled={loading}
                    >
                      <CurrencyIcon
                        asset={iconSymbol}
                        iconUrl={buildBaasStableCdnIconUrl(iconSymbol, baasBaseUrl)}
                        size={16}
                      />
                      {code}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {availableAssets.length > 0 ? (
          <div>
            <p className="mb-1.5 text-xs font-medium text-gray-500 dark:text-gray-400">
              {intl.get("baas.stablesWallet.create.networkLabel")}
            </p>
            <div className="flex flex-wrap gap-2">
              {networks.map((networkName) => {
                const selected = network === networkName;
                return (
                  <button
                    key={networkName}
                    type="button"
                    className={chipClass(selected)}
                    aria-pressed={selected}
                    onClick={() => setNetwork(networkName)}
                    disabled={loading || networksLoading}
                  >
                    {formatBaasStablesNetworkLabel(networkName)}
                  </button>
                );
              })}
            </div>
          </div>
          ) : null}
        </div>

        <div className="flex flex-wrap justify-end gap-3 border-t border-gray-100 px-5 py-4 dark:border-brand-dark-border sm:px-6">
          <button
            type="button"
            className="btn-outline w-auto"
            onClick={handleClose}
            disabled={loading}
          >
            {intl.get("baas.stablesWallet.create.cancel")}
          </button>
          <button
            type="submit"
            className="btn-primary w-auto"
            disabled={!canSubmit || accounts.length === 0}
          >
            {loading
              ? intl.get("baas.stablesWallet.create.submitting")
              : intl.get("baas.stablesWallet.create.submit")}
          </button>
        </div>
      </form>
    </Dialog>
  );
};

export default BaasStablesWalletCreateModal;
